import React, { useCallback, useMemo, useRef, useState } from 'react'
import { InputAdornment, Popover, TextField } from '@material-ui/core'
import { KeyboardArrowDown, HomeWork, Search } from '@material-ui/icons'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'src/contexts/auth'
import { useGetFarmList } from 'src/hooks/api/farm'
import { ConfirmationModal } from 'src/components/ConfirmationModal'
import ROUTES from 'src/constants/routes'
import { useStyles } from './styles'

export const FarmSelector: React.FC = () => {
  const classes = useStyles()
  const { user, userFarm, switchFarm } = useAuth()
  const { data: apiFarms, isLoading } = useGetFarmList()
  const navigate = useNavigate()
  const farms = apiFarms ?? user?.listPropriedades ?? []

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [pendingFarmId, setPendingFarmId] = useState<string | null>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const currentFarm = farms.find((f) => f.idFazenda === userFarm)

  const filteredFarms = useMemo(() => {
    const available = farms.filter((f) => f.idFazenda !== userFarm)
    if (!search.trim()) return available
    const term = search.toLowerCase().trim()
    return available.filter((f) => f.descricao?.toLowerCase().includes(term))
  }, [farms, search, userFarm])

  const scrollToItem = useCallback((index: number) => {
    const list = listRef.current
    if (!list) return
    const item = list.children[index] as HTMLElement | undefined
    item?.scrollIntoView({ block: 'nearest' })
  }, [])

  if (!isLoading && farms.length === 0) {
    return (
      <div className={classes.wrapper}>
        <span className={classes.farmLabel}>Sem propriedade</span>
      </div>
    )
  }

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (farms.length > 1) {
      setAnchorEl(e.currentTarget)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSearch('')
    setHighlightedIndex(-1)
  }

  const handleSelect = (farmId: string) => {
    setPendingFarmId(farmId)
    handleClose()
  }

  const handleConfirmSwitch = () => {
    if (pendingFarmId) {
      switchFarm(pendingFarmId)
      setPendingFarmId(null)
      navigate(ROUTES.DASHBOARD)
    }
  }

  const handleCancelSwitch = () => {
    setPendingFarmId(null)
  }

  const open = Boolean(anchorEl)

  return (
    <div className={classes.wrapper}>
      <button type='button' className={classes.trigger} onClick={handleOpen}>
        <HomeWork className={classes.farmIcon} />
        <span
          className={classes.triggerText}
          title={farms.length > 1 ? 'Clique para trocar de fazenda' : 'Fazenda atual'}
        >
          {currentFarm?.descricao || 'Selecionar fazenda'}
        </span>
        {farms.length > 1 && <KeyboardArrowDown className={classes.triggerIcon} />}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ className: classes.popoverPaper }}
        TransitionProps={{
          onEntered: () => searchInputRef.current?.focus()
        }}
      >
        <div className={classes.searchContainer}>
          <TextField
            autoFocus
            fullWidth
            size='small'
            placeholder='Buscar fazenda...'
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setHighlightedIndex(-1)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                const next = Math.min(highlightedIndex + 1, filteredFarms.length - 1)
                setHighlightedIndex(next)
                scrollToItem(next)
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                const prev = Math.max(highlightedIndex - 1, 0)
                setHighlightedIndex(prev)
                scrollToItem(prev)
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const farm = filteredFarms[highlightedIndex]
                if (farm?.idFazenda) handleSelect(farm.idFazenda)
              }
            }}
            variant='outlined'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search className={classes.searchIcon} />
                </InputAdornment>
              ),
              classes: { root: classes.searchInput }
            }}
          />
        </div>

        <ul ref={listRef} className={classes.farmList}>
          {filteredFarms.length === 0 ? (
            <li className={classes.emptyResult}>Nenhuma fazenda encontrada</li>
          ) : (
            filteredFarms.map((farm, index) => (
              <li key={farm.idFazenda}>
                <button
                  type='button'
                  className={[classes.farmItem, index === highlightedIndex ? classes.farmItemHighlighted : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => farm.idFazenda && handleSelect(farm.idFazenda)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <span className={classes.farmItemName}>{farm.descricao}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </Popover>

      <ConfirmationModal
        isOpen={!!pendingFarmId}
        title='Trocar de propriedade'
        message='Ao trocar de propriedade, todos os dados serão carregados de acordo com a nova propriedade selecionada.'
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
        confirmText='Confirmar'
        cancelText='Cancelar'
      />
    </div>
  )
}
